/* assets/collection.js — pure JS, no <script> tags */
window.Collection = class Collection {
  constructor() {
    this.selectors = {
      section: ".facest-filters-section",
      toolbar: ".m-collection-toolbar",
      cols: [".m-toolbar--column-switcher button"],
      productContainer: "[data-product-container]",
      loadMoreBtn: "[data-load-more]",
      sidebar: ".m-sidebar",
      sidebarContent: ".m-sidebar--content",
      openSidebar: ".m-sidebar--open",
      closeSidebar: ".m-sidebar--close",
      filtersWrapper: ".m-filter--wrapper"
    };
    this.mobileSortingSelectors = {
      openSorting: ".m-sortby--open",
      closeSorting: ".m-sortby-mobile--close",
      sortingWrapper: ".m-sortby-mobile--wrapper",
      sortingList: ".m-sortby-mobile--content"
    };
    this.sectionId = "";
    this.isDesignMode = MinimogSettings.design_mode;
    this.currentPage = 1;
    this.paginationType = undefined;
    this.totalPages = undefined;
    this.totalProducts = undefined;
    this.infiniteLoadingObserver = undefined;
    this.showColSwitchers = undefined;
    this.cachedCol = undefined;
    this.initialCol = undefined;
    this.activeCol = undefined;
    this.STORAGE_KEY = "gridColumnViews";
    this.MAX_COL_BY_SCREEN_SIZE = { mobile: 2, tablet: 4, desktop: 5 };
    this.enableFilters = undefined;
    this.enableSorting = undefined;
    this.filtersType = undefined;
    this.filtersPosition = undefined;
    this.accordions = undefined;
    this.sideEffectEventsAdded = false;
  }

  init = () => {
    this.domNodes = queryDomNodes(this.selectors);
    this.mbSortingNodes = queryDomNodes(this.mobileSortingSelectors);
    this.setData();
    this.initGridView();
    this.initFilters();
    this.initMobileSorting();
    this.initProducts();
    this.sideEffectEventsAdded = true;
    MinimogEvents.subscribe("ON_PRODUCT_LIST_UPDATED", () => refreshProductReview());
    this.handleStickyToolbar();
    document.addEventListener("matchMobile", () => this.handleStickyToolbar());
    document.addEventListener("unmatchMobile", () => this.handleStickyToolbar());
  };

  setData = () => {
    const { section: t, productContainer: e } = this.domNodes;
    this.sectionId = t.dataset.sectionId;
    this.paginationType = t.dataset.paginationType;
    this.totalPages = e && Number(e.dataset.totalPages);
    this.view = t.dataset.view;
    this.activeCol = null;
    this.currentPage = 1;
    this.initialCol = Number(t.dataset.initialColumn);
    this.cachedCol = Number(localStorage.getItem(this.STORAGE_KEY)) || this.initialCol;
    this.showColSwitchers = t.dataset.showColSwitchers === "true";
    this.enableFilters = t.dataset.enableFilters === "true";
    this.enableSorting = t.dataset.enableSorting === "true";
    this.filtersType = t.dataset.filtersType;
    this.filtersPosition = t.dataset.filtersPosition;
  };

  initGridView = () => {
    this.toggleView(this.isDesignMode ? this.initialCol : this.cachedCol);
    this.updateViewByScreen();
    addEventDelegate({
      selector: this.selectors.cols[0],
      context: this.domNodes.toolbar,
      handler: (evt, el) => this.toggleView(Number(el.dataset.column))
    });
    this.initLoadMore();
    if (!this.sideEffectEventsAdded) {
      window.addEventListener("resize", debounce(this.updateViewByScreen, 500));
    }
  };

  initFilters = () => {
    if (!this.enableFilters) return;
    const { sidebar: t, openSidebar: e, closeSidebar: i } = this.domNodes;
    requestAnimationFrame(this.initAccordions);
    e.addEventListener("click", this.openSidebarFilter);
    i.addEventListener("click", this.closeSidebarFilter);
    t.addEventListener("click", (ev) => ev.target === t && this.closeSidebarFilter());
  };

  initMobileSorting = () => {
    if (!this.enableSorting) return;
    const { openSorting: t, closeSorting: e, sortingWrapper: i } = this.mbSortingNodes;
    t.addEventListener("click", this.openMobileSorting);
    e.addEventListener("click", this.closeMobileSorting);
    i.addEventListener("click", (ev) => ev.target === i && this.closeMobileSorting());
  };

  openMobileSorting = () => {
    const { sortingWrapper: t, sortingList: e } = this.mbSortingNodes;
    t.style.display = "block";
    requestAnimationFrame(() => {
      t.style.setProperty("--m-bg-opacity", "0.5");
      e.style.setProperty("--m-translate-y", "0");
    });
  };

  closeMobileSorting = () => {
    const { sortingWrapper: e, sortingList: i } = this.mbSortingNodes;
    e.style.setProperty("--m-bg-opacity", "0");
    i.style.setProperty("--m-translate-y", "100%");
    setTimeout(() => e.style.setProperty("display", "none"), 300);
  };

  initAccordions = () => {
    this.accordions && this.accordions.destroy();
    const { filtersWrapper: t } = this.domNodes;
    t.classList.remove("acc-initialized");
    this.accordions = new MinimogLibs.Accordion(t, {
      presetContentHeight: window.innerWidth > 1280 && this.filtersPosition !== "fixed",
      callback: () => t.style.setProperty("opacity", "1")
    });
  };

  openSidebarFilter = () => {
    const { sidebar: t, sidebarContent: e, section: i } = this.domNodes;
    t.style.display = "block";
    requestAnimationFrame(() => {
      t.style.setProperty("--m-bg-opacity", "0.5");
      e.style.setProperty("--m-translate-x", "0");
      this.accordions && this.accordions.setContentHeight();
    });
    i.classList.add("sidebar-open");
  };

  closeSidebarFilter = () => {
    const { sidebar: t, sidebarContent: e, section: i } = this.domNodes;
    i.classList.remove("sidebar-open");
    if (window.innerWidth < 1280 || t.dataset.type === "fixed") {
      e.style.setProperty("--m-translate-x", "-100%");
      t.style.removeProperty("--m-bg-opacity");
      setTimeout(() => t.style.removeProperty("display"), 300);
    }
  };

  toggleView = (t) => {
    if (!t || this.view === "search") return;
    const { cols: e, productContainer: i } = this.domNodes;
    if (!i) return;

    const imgs = i.querySelectorAll("responsive-image");
    imgs && imgs.forEach((n) => { n.disconnectedCallback(); n.connectedCallback(); });

    const prev = this.activeCol || this.initialCol;
    if (this.showColSwitchers) {
      e[prev - 1].classList.remove("active");
      e[t - 1].classList.add("active");
    }
    i.classList.remove(`m-cols-${prev}`);
    i.classList.add(`m-cols-${t}`);
    localStorage.setItem(this.STORAGE_KEY, t);
    this.activeCol = t;
  };

  updateViewByScreen = () => {
    if (this.activeCol === 1) return;
    const dev = window.innerWidth < 768 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop";
    const max = this.MAX_COL_BY_SCREEN_SIZE[dev];
    if (max < this.activeCol) this.toggleView(max);
  };

  initLoadMore = () => {
    if (this.paginationType === "paginate" || this.totalPages <= 1 || this.view === "search") return;
    const { loadMoreBtn: t } = this.domNodes;
    t.addEventListener("click", this.loadMoreProducts);
    if (this.paginationType === "infinite") {
      this.infiniteLoadingObserver = new IntersectionObserver(
        (entries) => entries.forEach((en) => en.intersectionRatio === 1 && this.loadMoreProducts()),
        { threshold: 1 }
      );
      this.infiniteLoadingObserver.observe(t);
    }
  };

  loadMoreProducts = () => {
    const next = this.currentPage + 1;
    if (next > this.totalPages) return;
    const { productContainer: e, loadMoreBtn: i } = this.domNodes;
    this.toggleLoading(true);
    fetchSection(this.sectionId, { fromCache: true, params: { page: next } })
      .then((doc) => {
        doc.querySelectorAll("[data-product-container] .m-product-item").forEach((n) => e.appendChild(n));
        this.initProducts();
      })
      .finally(() => {
        MinimogEvents.emit("ON_PRODUCT_LIST_UPDATED");
        this.toggleLoading(false);
        this.currentPage = next;
        if (next >= this.totalPages) {
          i.parentNode.remove();
          this.infiniteLoadingObserver && this.infiniteLoadingObserver.unobserve(i);
        }
      });
  };

  initProducts = () => {
    MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState();
    MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState();
  };

  toggleLoading = (on) => {
    const { loadMoreBtn: e } = this.domNodes;
    if (e) e.classList[on ? "add" : "remove"]("m-spinner-loading");
  };

  handleStickyToolbar() {
    const t = this.domNodes.toolbar, top = t && t.offsetTop;
    let last = 0;
    window.addEventListener("scroll", () => {
      if (MinimogTheme.config.mqlMobile) {
        const y = window.scrollY;
        if (y <= top) { t.classList.remove("scroll-up","m-collection-toolbar--sticky"); return; }
        t.classList.add("m-collection-toolbar--sticky");
        if (y > t.offsetHeight + top && y > last && !t.classList.contains("scroll-down")) {
          t.classList.remove("scroll-up"); t.classList.add("scroll-down");
        } else if (y < last && t.classList.contains("scroll-down")) {
          t.classList.remove("scroll-down"); t.classList.add("scroll-up");
        }
        last = y;
      } else {
        t.classList.remove("scroll-up","m-collection-toolbar--sticky","scroll-down");
      }
    });
  }
};
